import type { ItemKind, Rules } from "odyssey-shared";
import { itemName } from "./describe.ts";
import type { BagSlot } from "./snapshot.ts";

/**
 * Search and filter in the Inventory window (P2.8-T45, ADR-125 decision 2): views on the page that change what the
 * window draws and never the bag, so nothing here is sent. Pure: the window and the tool row read these.
 *
 * - Search matches the item's name as the page shows it, anywhere in the name, ignoring case and accents: both sides
 *   are decomposed (NFKD), their combining marks dropped, then lower-cased, so "pot" and "PÖT" both find the Minor
 *   Healing Potion.
 * - Filter shows one kind, in the design's fixed order after All; a kind's category holds every stack of that kind.
 * - Both apply together: a stack matches when it passes both. An empty slot never matches.
 */

/**
 * The most characters the search field takes.
 */
export const BAG_SEARCH_MAX = 32;

/**
 * What Filter shows: every stack, or the stacks of one item kind.
 */
export type BagFilter = "all" | ItemKind;

/**
 * Filter's entries in the design's order: All, then Gear, Consumables, Materials and Quest. The design's "Other" is
 * left out, since every kind has a category of its own (ADR-125 decision 2.3).
 */
export const BAG_FILTERS: readonly BagFilter[] = ["all", "equipment", "consumable", "material", "quest"];

/**
 * What the window is looking for: the search text as typed and the filter chosen.
 */
export interface BagLook {
  readonly query: string;
  readonly filter: BagFilter;
}

/**
 * Looking for nothing: no text, and All.
 */
export const NO_LOOK: BagLook = { query: "", filter: "all" };

/**
 * What a look finds in the bag: for each slot whether it holds a stack that matches, how many stacks match, and how
 * many stacks the bag holds.
 */
export interface BagLookResult {
  readonly matches: readonly boolean[];
  readonly hits: number;
  readonly stacks: number;
}

/**
 * The form a name and a search are compared in: decomposed, without combining marks, lower-cased.
 *
 * @param text The text.
 * @returns Its comparison key.
 * @example
 * searchKey("PÖT");
 * // "pot"
 */
export function searchKey(text: string): string {
  return text.normalize("NFKD").replace(/\p{M}/gu, "").toLowerCase();
}

/**
 * Whether a look narrows anything: text in the field or a filter other than All.
 *
 * @param look The look.
 * @returns True while looking.
 */
export function isLooking(look: BagLook): boolean {
  return look.query !== "" || look.filter !== "all";
}

/**
 * Whether one item passes a look: its name as the page shows it holds the search's key, and its kind is the filter's.
 * An id the rules lack is named "Unknown item" and has no kind, so it passes All alone.
 *
 * @param rules The page's rules.
 * @param item The item's rules id.
 * @param key The search's comparison key (`searchKey`).
 * @param filter The filter.
 * @returns True for a match.
 */
function itemMatches(rules: Rules, item: string, key: string, filter: BagFilter): boolean {
  if (filter !== "all" && rules.items.get(item)?.kind !== filter) {
    return false;
  }
  return key === "" || searchKey(itemName(rules, item)).includes(key);
}

/**
 * What a look finds among the bag's slots.
 *
 * @param rules The page's rules.
 * @param slots The bag's slots in slot order.
 * @param look The look.
 * @returns Each slot's match, the matching stacks and the stacks held.
 * @example
 * lookAtBag(PAGE_RULES, [{ item: "minor_healing_potion", quantity: 5, pending: false }], { query: "pot", filter: "all" });
 * // { matches: [true], hits: 1, stacks: 1 }
 */
export function lookAtBag(rules: Rules, slots: readonly BagSlot[], look: BagLook): BagLookResult {
  const key = searchKey(look.query);
  let hits = 0;
  let stacks = 0;
  const matches = slots.map((slot) => {
    if (slot.item === null) {
      return false;
    }
    stacks += 1;
    const match = itemMatches(rules, slot.item, key, look.filter);
    if (match) {
      hits += 1;
    }
    return match;
  });
  return { matches, hits, stacks };
}

/**
 * How many stacks each of Filter's entries holds: All every stack, a kind its own.
 *
 * @param rules The page's rules.
 * @param slots The bag's slots.
 * @returns The count for each entry.
 * @example
 * filterCounts(PAGE_RULES, [{ item: "minor_healing_potion", quantity: 5, pending: false }]);
 * // { all: 1, equipment: 0, consumable: 1, material: 0, quest: 0 }
 */
export function filterCounts(rules: Rules, slots: readonly BagSlot[]): Readonly<Record<BagFilter, number>> {
  const counts: Record<BagFilter, number> = { all: 0, equipment: 0, consumable: 0, material: 0, quest: 0 };
  for (const slot of slots) {
    if (slot.item === null) {
      continue;
    }
    counts.all += 1;
    const kind = rules.items.get(slot.item)?.kind;
    if (kind !== undefined) {
      counts[kind] += 1;
    }
  }
  return counts;
}
